import { BaseApiMessage } from '@/types/api'
import { defineStore } from 'pinia'
import { onMounted, ref } from 'vue'
import { useCustomGPT } from '@/stores/customGPT'
import { useChatStream } from '@/composables/useChatStream'
import { useCitationsStore } from '@/stores/citationsStore'
import { useCustomParams } from '@/composables/useCustomParams'

export const useConversationStore = defineStore('conversationStore', () => {
  const { prompt } = useCustomParams()
  const promptRef = ref<HTMLTextAreaElement | null>(null)
  const promptMessage = ref('')

  const isThinking = ref(false)
  const isAnswering = ref(false)
  const isPreparing = ref(true)
  const messages = ref<BaseApiMessage[]>([])
  const answer = ref<BaseApiMessage | null>(null)
  const stopChatStreamHandler = ref<() => void>()

  const { getCitationsForPrompt } = useCitationsStore()
  const customGPTstore = useCustomGPT()
  const { sendMessageToConversation, createConversation } = customGPTstore

  const focus = () => {
    promptRef.value?.focus()
  }

  const resetMessages = () => {
    messages.value = []
  }

  const handleSendMessage = async () => {
    stopChatStreamHandler.value?.call(null)
    const newMessage = {
      content: promptMessage.value,
      role: 'user'
    } as BaseApiMessage
    messages.value.push(newMessage)

    isThinking.value = true
    const stream = await sendMessageToConversation(promptMessage.value)
    isAnswering.value = true

    const { stopChatStream } = useChatStream({
      stream,
      onChunk: ({ data }) => {
        isAnswering.value = true
        if (isThinking.value) {
          isThinking.value = false
          answer.value = {
            content: '',
            role: 'assistant'
          } as BaseApiMessage
          messages.value.push(answer.value)
        }
        answer.value!.content += data
      },
      onReady: ({ data }) => {
        if (data) {
          answer.value!.citations = getCitationsForPrompt(newMessage.content, data.citations)
          answer.value!.id = `${data.id}`
        }
        isAnswering.value = false
        focus()
      }
    })

    stopChatStreamHandler.value = stopChatStream
  }

  const handleNewConversation = async () => {
    isPreparing.value = true
    await createConversation()
    resetMessages()
    isThinking.value = false
    isAnswering.value = false
    isPreparing.value = false
  }

  const handleStop = () => {
    stopChatStreamHandler.value?.call(null)
    isAnswering.value = false
    isThinking.value = false
    focus()
  }

  onMounted(async () => {
    if (prompt) {
      promptMessage.value = prompt as string
      isPreparing.value = false
      await handleSendMessage()
    } else {
      isPreparing.value = false
    }
  })

  return {
    handleSendMessage,
    handleNewConversation,
    handleStop,
    resetMessages,
    focus,
    promptRef,
    promptMessage,
    isThinking,
    isAnswering,
    isPreparing,
    stopChatStreamHandler,
    messages
  }
})
